import { slugify } from './slug.ts';

export type FormStatus = 'draft' | 'published' | 'archived';
export type FormAvailabilityState = 'draft' | 'scheduled' | 'live' | 'expired' | 'archived';

export type FormFieldType =
  | 'section'
  | 'text'
  | 'text_input'
  | 'textarea'
  | 'email'
  | 'phone'
  | 'number'
  | 'date'
  | 'person_list'
  | 'select'
  | 'multiselect'
  | 'radio'
  | 'checkbox'
  | 'checkbox_group';

export type FormRow = {
  id: string;
  title: string;
  slug: string;
  short_description: string;
  intro_text: string;
  submit_button_label: string;
  confirmation_title: string;
  confirmation_message: string;
  allow_submission_edit: boolean;
  status: FormStatus;
  starts_at: string | null;
  ends_at: string | null;
  show_on_homepage: boolean;
  homepage_title: string | null;
  homepage_text: string | null;
  homepage_cta_label: string | null;
  push_body: string | null;
  published_at: string | null;
  notification_sent_at: string | null;
  notification_sent_by: string | null;
  last_status: FormStatus | null;
  public_url: string;
  created_by: string;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
};

export type FormFieldRow = {
  id: string;
  form_id: string;
  field_key: string;
  field_type: FormFieldType;
  label: string;
  help_text: string;
  placeholder: string;
  static_text: string;
  options: string[];
  field_settings: Record<string, unknown>;
  is_required: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export type FormSubmissionRow = {
  id: string;
  form_id: string;
  user_id: string | null;
  source: 'app' | 'web';
  source_ip_hash: string | null;
  user_agent: string | null;
  test_submission: boolean;
  submitted_at: string;
};

export type FormSubmissionAnswerRow = {
  submission_id: string;
  form_id: string;
  field_id: string | null;
  field_key: string;
  field_label: string;
  field_type: Exclude<FormFieldType, 'section' | 'text'>;
  field_order: number;
  value_text: string | null;
  value_json: unknown;
  created_at: string;
};

export type FormSubmissionWithAnswers = FormSubmissionRow & {
  answers: FormSubmissionAnswerRow[];
};

export type FormPersonListEntry = {
  displayName: string;
  userId: string | null;
  source: 'profile' | 'manual';
};

export type FormProfileSuggestion = {
  userId: string;
  displayName: string;
  firstName: string;
  lastName: string;
  nickname: string;
};

export type FormRoomPlanningConfigRow = {
  form_id: string;
  enabled: boolean;
  person_list_field_id: string | null;
  suggestions_enabled: boolean;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
};

export type FormRoomContingentRow = {
  id: string;
  form_id: string;
  room_count: number;
  capacity: number;
  label_prefix: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export type FormRoomRow = {
  id: string;
  form_id: string;
  contingent_id: string | null;
  name: string;
  capacity: number;
  floor: string;
  type: string;
  is_sleeping_room: boolean;
  notes: string;
  sort_order: number;
  is_locked: boolean;
  created_at: string;
  updated_at: string;
};

export type FormRoomAssignmentRow = {
  id: string;
  form_id: string;
  room_id: string | null;
  submission_id: string | null;
  user_id: string | null;
  display_name: string;
  source: 'submission' | 'profile' | 'manual' | 'external';
  sort_order: number;
  notes: string;
  locked: boolean;
  created_at: string;
  updated_at: string;
};

export function submissionSourceLabel(source: FormSubmissionRow['source']): string {
  return source === 'app' ? 'App' : 'Web';
}

export function submissionSourceColor(source: FormSubmissionRow['source']): string {
  return source === 'app' ? 'teal' : 'gray';
}

export const FORM_FIELD_TYPE_OPTIONS: Array<{ value: FormFieldType; label: string }> = [
  { value: 'section', label: 'Abschnittsüberschrift' },
  { value: 'text', label: 'Text / Hinweistext' },
  { value: 'text_input', label: 'Einzeiliger Text' },
  { value: 'textarea', label: 'Mehrzeiliger Text' },
  { value: 'email', label: 'E-Mail' },
  { value: 'phone', label: 'Telefonnummer' },
  { value: 'number', label: 'Zahl' },
  { value: 'date', label: 'Datum' },
  { value: 'person_list', label: 'Mehrere Personen / Zimmerwunsch' },
  { value: 'select', label: 'Dropdown' },
  { value: 'multiselect', label: 'Multi-Select' },
  { value: 'radio', label: 'Radio' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'checkbox_group', label: 'Checkbox-Gruppe' },
];

export function fieldKeyFromLabel(label: string): string {
  const next = slugify(label).replace(/-/g, '_').slice(0, 64);
  return next || 'feld';
}

export function isStaticField(fieldType: FormFieldType): boolean {
  return fieldType === 'section' || fieldType === 'text';
}

export function isSelectionField(fieldType: FormFieldType): boolean {
  return fieldType === 'select'
    || fieldType === 'multiselect'
    || fieldType === 'radio'
    || fieldType === 'checkbox_group';
}

export function isMultiValueField(fieldType: FormFieldType): boolean {
  return fieldType === 'multiselect' || fieldType === 'checkbox_group';
}

export function isPersonListField(fieldType: FormFieldType): boolean {
  return fieldType === 'person_list';
}

export function personListUsesProfileSuggestions(field: Pick<FormFieldRow, 'field_type' | 'field_settings'>): boolean {
  if (!isPersonListField(field.field_type)) return false;
  return field.field_settings.profileSuggestions !== false;
}

export function profileDisplayName(profile: Record<string, unknown>): string {
  const firstName = String(profile.first_name ?? profile.firstName ?? '').trim();
  const lastName = String(profile.last_name ?? profile.lastName ?? '').trim();
  const nickname = String(profile.nickname ?? '').trim();
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  return fullName || nickname;
}

export function normalizeProfileSuggestion(profile: Record<string, unknown>): FormProfileSuggestion | null {
  const userId = String(profile.id ?? profile.user_id ?? profile.userId ?? '').trim();
  const displayName = profileDisplayName(profile);
  if (!userId || !displayName) return null;
  return {
    userId,
    displayName,
    firstName: String(profile.first_name ?? profile.firstName ?? '').trim(),
    lastName: String(profile.last_name ?? profile.lastName ?? '').trim(),
    nickname: String(profile.nickname ?? '').trim(),
  };
}

export function findProfileSuggestions(
  query: string,
  profiles: Array<Record<string, unknown>>,
  limit = 12,
): FormProfileSuggestion[] {
  const token = normalizePersonMatchToken(query);
  if (!token) return [];

  const results: FormProfileSuggestion[] = [];
  const seen = new Set<string>();
  for (const rawProfile of profiles) {
    const profile = normalizeProfileSuggestion(rawProfile);
    if (!profile || seen.has(profile.userId)) continue;
    const haystack = normalizePersonMatchToken([
      profile.firstName,
      profile.lastName,
      profile.displayName,
      profile.nickname,
      [profile.nickname, profile.lastName].filter(Boolean).join(' '),
    ].join(' '));
    if (!haystack.includes(token)) continue;
    seen.add(profile.userId);
    results.push(profile);
    if (results.length >= limit) break;
  }
  return results;
}

export function personListEntryFromProfile(profile: FormProfileSuggestion): FormPersonListEntry {
  return {
    displayName: profile.displayName,
    userId: profile.userId,
    source: 'profile',
  };
}

export function personListEntryFromText(value: string): FormPersonListEntry {
  const displayName = value.trim();
  return { displayName, userId: null, source: 'manual' };
}

function normalizePersonListEntry(value: unknown): FormPersonListEntry | null {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    const displayName = String(value).trim();
    return displayName ? { displayName, userId: null, source: 'manual' } : null;
  }

  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const displayName = String(row.displayName ?? row.display_name ?? row.name ?? '').trim();
  if (!displayName) return null;
  const userId = String(row.userId ?? row.user_id ?? '').trim();
  const rawSource = String(row.source ?? '').trim();
  const source: 'profile' | 'manual' =
    rawSource === 'profile' || rawSource === 'manual'
      ? rawSource
      : userId ? 'profile' : 'manual';
  return {
    displayName,
    userId: userId || null,
    source,
  };
}

export function normalizeFormPersonListEntries(value: unknown): FormPersonListEntry[] {
  const rawEntries = Array.isArray(value) ? value : [value];
  const entries: FormPersonListEntry[] = [];
  const seen = new Set<string>();

  for (const rawEntry of rawEntries) {
    const entry = normalizePersonListEntry(rawEntry);
    if (!entry) continue;
    const key = `${entry.userId ?? ''}:${normalizePersonMatchToken(entry.displayName)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    entries.push(entry);
  }

  return entries;
}

export function normalizePersonListEntries(value: unknown): string[] {
  return normalizeFormPersonListEntries(value).map((entry) => entry.displayName);
}

export function normalizePersonMatchToken(value: string): string {
  return value
    .toLocaleLowerCase('de-DE')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\p{L}\p{N}\s+-]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function statusLabel(status: FormStatus): string {
  switch (status) {
    case 'draft':
      return 'Entwurf';
    case 'published':
      return 'Veröffentlicht';
    case 'archived':
      return 'Archiviert';
    default:
      return status;
  }
}

export function statusColor(status: FormStatus): string {
  switch (status) {
    case 'draft':
      return 'gray';
    case 'published':
      return 'teal';
    case 'archived':
      return 'yellow';
    default:
      return 'gray';
  }
}

export function linesToOptions(raw: string): string[] {
  return Array.from(
    new Set(
      raw
        .split('\n')
        .map((part) => part.trim())
        .filter(Boolean),
    ),
  );
}

export function optionsToLines(options: string[]): string {
  return options.join('\n');
}

export function formatStoredAnswerValue(answer: Pick<FormSubmissionAnswerRow, 'value_text' | 'value_json' | 'field_type'>): string {
  if (typeof answer.value_text === 'string' && answer.value_text.trim().length > 0) {
    return answer.value_text.trim();
  }

  if (answer.field_type === 'checkbox' && typeof answer.value_json === 'boolean') {
    return answer.value_json ? 'Ja' : 'Nein';
  }

  if (Array.isArray(answer.value_json)) {
    if (answer.field_type === 'person_list') {
      return normalizePersonListEntries(answer.value_json).join(' | ');
    }
    return answer.value_json.map((entry) => String(entry)).join(' | ');
  }

  if (answer.value_json == null) {
    return '';
  }

  if (typeof answer.value_json === 'string') {
    return answer.value_json;
  }

  return String(answer.value_json);
}

export function isFormOpenNow(form: Pick<FormRow, 'status' | 'starts_at' | 'ends_at'>, now = new Date()): boolean {
  if (form.status !== 'published') return false;

  const startsAt = form.starts_at ? new Date(form.starts_at) : null;
  const endsAt = form.ends_at ? new Date(form.ends_at) : null;

  if (startsAt && startsAt.getTime() > now.getTime()) return false;
  if (endsAt && endsAt.getTime() < now.getTime()) return false;
  return true;
}

export function getFormAvailabilityState(
  form: Pick<FormRow, 'status' | 'starts_at' | 'ends_at'>,
  now = new Date(),
): FormAvailabilityState {
  if (form.status === 'draft') return 'draft';
  if (form.status === 'archived') return 'archived';
  if (isFormOpenNow(form, now)) return 'live';

  const startsAt = form.starts_at ? new Date(form.starts_at) : null;
  const endsAt = form.ends_at ? new Date(form.ends_at) : null;

  if (startsAt && startsAt.getTime() > now.getTime()) return 'scheduled';
  if (endsAt && endsAt.getTime() < now.getTime()) return 'expired';
  return 'draft';
}

export function availabilityLabel(state: FormAvailabilityState): string {
  switch (state) {
    case 'draft':
      return 'Nur Entwurf';
    case 'scheduled':
      return 'Geplant';
    case 'live':
      return 'Jetzt öffentlich';
    case 'expired':
      return 'Nicht mehr aktiv';
    case 'archived':
      return 'Archiviert';
    default:
      return state;
  }
}

export function availabilityColor(state: FormAvailabilityState): string {
  switch (state) {
    case 'draft':
      return 'gray';
    case 'scheduled':
      return 'blue';
    case 'live':
      return 'teal';
    case 'expired':
      return 'orange';
    case 'archived':
      return 'yellow';
    default:
      return 'gray';
  }
}

export const DEFAULT_FORM_PUSH_TITLE = 'Neue Anmeldung verfügbar';
export const FORM_PUSH_BODY_MAX_LENGTH = 180;

export function clipFormPushText(raw: string, maxLength = FORM_PUSH_BODY_MAX_LENGTH): string {
  const compact = raw.replace(/\s+/g, ' ').trim();
  if (compact.length <= maxLength) return compact;
  return `${compact.slice(0, maxLength - 1).trimEnd()}…`;
}

export function buildDefaultFormPushBody(title: string): string {
  const cleanTitle = title.trim();
  if (!cleanTitle) {
    return 'Eine neue Anmeldung ist jetzt in der App verfügbar.';
  }
  return `„${cleanTitle}“ ist jetzt in der App verfügbar.`;
}

export function resolveFormPushBody(form: Pick<FormRow, 'title' | 'push_body'>): string {
  const custom = form.push_body?.trim() ?? '';
  return clipFormPushText(custom || buildDefaultFormPushBody(form.title));
}

export function buildSubmissionPreview(submission: FormSubmissionWithAnswers): string {
  const previewParts = submission.answers
    .map((answer) => {
      const value = formatStoredAnswerValue(answer);
      if (!value) return '';
      return `${answer.field_label}: ${value}`;
    })
    .filter(Boolean);

  return previewParts.slice(0, 3).join(' · ');
}
